import { eraseCookie, getCookie } from "~/v2/utils/cookies";

export const GROWSURF_REFERRER_COOKIE = "alpaca_growsurf_referrer_id";

const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
] as const;

type UtmKey = (typeof UTM_KEYS)[number];
type UtmParams = Partial<Record<UtmKey, string>>;
type GrowSurfReferralParams = {
  growsurf_referrer_id?: string;
};
type SignupAttributionParams = UtmParams & GrowSurfReferralParams;

const GROWSURF_REFERRER_ID_PATTERN = /^[A-Za-z0-9_-]{1,128}$/;

// Long-lived referrer cookies take precedence over URL UTM params.
const REFERRER_COOKIE_BY_UTM_KEY: Record<UtmKey, string> = {
  utm_source: "alpaca_referrer_campaign_source",
  utm_medium: "alpaca_referrer_campaign_medium",
  utm_campaign: "alpaca_referrer_campaign_name",
  utm_content: "alpaca_referrer_campaign_content",
  utm_term: "alpaca_referrer_campaign_term",
};

export const parseUtmParams = (params: URLSearchParams): UtmParams => {
  const utm_params: UtmParams = {};

  for (const utm_key of UTM_KEYS) {
    const value = params.get(utm_key);
    if (value !== null) {
      utm_params[utm_key] = value;
    }

    // override utm_params with long lived referrer cookies
    const cookieValue = getCookie(REFERRER_COOKIE_BY_UTM_KEY[utm_key]);
    if (cookieValue) {
      utm_params[utm_key] = cookieValue;
    }
  }

  return utm_params;
};

export const parseGrowSurfReferralParams = (
  params: URLSearchParams,
): GrowSurfReferralParams => {
  const referrerID =
    normalizeGrowSurfReferrerID(getCookie(GROWSURF_REFERRER_COOKIE)) ??
    normalizeGrowSurfReferrerID(params.get("grsf"));

  if (!referrerID) {
    return {};
  }

  return {
    growsurf_referrer_id: referrerID,
  };
};

export const normalizeGrowSurfReferrerID = (
  value: unknown,
): string | undefined => {
  if (typeof value !== "string") {
    return undefined;
  }

  const referrerID = value.trim();
  return GROWSURF_REFERRER_ID_PATTERN.test(referrerID) ? referrerID : undefined;
};

export const parseSignupAttributionParams = (
  params: URLSearchParams,
): SignupAttributionParams => ({
  ...parseUtmParams(params),
  ...parseGrowSurfReferralParams(params),
});

export const clearGrowSurfReferralCookie = (): void => {
  eraseCookie(GROWSURF_REFERRER_COOKIE);
  eraseCookie(GROWSURF_REFERRER_COOKIE, "alpaca.markets");
};
